import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import type { ProjectKind, ProjectTransferDto } from '@aiai/shared';
import { acceptTransfer, declineTransfer, fetchIncomingTransfers } from '../api';
import { qk } from '../api/queryKeys';
import { formatDateTime } from '../lib/format';
import { transferErrorMessage } from '../lib/transferMessages';
import { useCapabilities } from '../hooks/useCapabilities';
import { Callout } from './Callout';
import { Spinner } from './Spinner';

type Decision = { slug: string; transfer: ProjectTransferDto; action: 'accept' | 'decline' };

/**
 * Pending transfers INTO any workspace the caller owns, with Accept / Decline.
 * Shown on the global Projects / Prototypes pages (filtered to that `kind`);
 * renders nothing when there are none. Only owners can answer, so only owned
 * workspaces are asked.
 */
export function IncomingTransfersBanner({ kind }: { kind: ProjectKind }) {
  const queryClient = useQueryClient();
  const { workspacesWith } = useCapabilities();
  const owned = workspacesWith('workspace:manage');

  const results = useQueries({
    queries: owned.map((o) => ({
      queryKey: qk.incomingTransfers(o.slug),
      queryFn: () => fetchIncomingTransfers(o.slug),
    })),
  });
  const incoming = owned.flatMap((o, i) =>
    (results[i]?.data ?? [])
      .filter((t) => t.status === 'pending' && t.project.kind === kind)
      .map((transfer) => ({ slug: o.slug, transfer })),
  );

  const decide = useMutation({
    mutationFn: async ({ slug, transfer, action }: Decision) => {
      if (action === 'accept') await acceptTransfer(slug, transfer.id);
      else await declineTransfer(slug, transfer.id);
    },
    onSuccess: (_res, { action }) => {
      queryClient.invalidateQueries({ queryKey: qk.transfers });
      if (action === 'accept') {
        for (const key of [qk.myProjects, qk.myPrototypes, qk.projects, qk.prototypes]) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });

  if (incoming.length === 0) return null;
  const noun = kind === 'prototype' ? 'prototype' : 'project';

  return (
    <div className="space-y-2" aria-label="Incoming transfers">
      {incoming.map(({ slug, transfer: t }) => {
        const busy = decide.isPending && decide.variables?.transfer.id === t.id;
        const failed = decide.isError && decide.variables?.transfer.id === t.id;
        return (
          <Callout
            key={t.id}
            tone="info"
            kicker={`Incoming ${noun} transfer`}
            actions={
              <>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ slug, transfer: t, action: 'accept' })}
                >
                  {busy && decide.variables?.action === 'accept' && <Spinner />}
                  Accept
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ slug, transfer: t, action: 'decline' })}
                >
                  {busy && decide.variables?.action === 'decline' && <Spinner />}
                  Decline
                </button>
              </>
            }
          >
            <span className="font-medium text-ink">{t.fromOrg.name}</span>
            {t.createdByEmail ? ` (${t.createdByEmail})` : ''} wants to move{' '}
            <span className="font-medium text-ink">{t.project.name}</span> into your workspace{' '}
            <span className="font-medium text-ink">{t.toOrg.name}</span>. Expires{' '}
            {formatDateTime(t.expiresAt)}.
            {failed && (
              <span className="mt-1 block text-danger">
                {transferErrorMessage(decide.error, 'Could not answer the transfer')}
              </span>
            )}
          </Callout>
        );
      })}
    </div>
  );
}
